/**
 * Enveloppe les écrans réservés aux adultes : sans code parent défini, rien
 * ne change ; avec un code, il faut le saisir (voir `lib/parentGate.tsx` pour
 * la durée de validité). L'écran de saisie se rend dans le `Chrome` de la
 * route, donc la barre d'onglets reste visible : l'enfant n'est jamais piégé,
 * et « Retour » le ramène à l'accueil.
 */
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PinPad } from "./PinPad";
import { Button, MutedText, PageLayout } from "./ui";
import { useAuth } from "../lib/auth";
import { useParentGate } from "../lib/parentGate";
import { requestPinReset, verifyKidPin } from "../lib/dataSource";
import { isRateLimitedError } from "../lib/api";

function ForgotPin() {
  const { t } = useTranslation();
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  async function send() {
    setState("sending");
    try {
      await requestPinReset();
      setState("sent");
    } catch {
      setState("error");
    }
  }

  if (state === "sent") {
    return <MutedText className="text-center max-w-xs">{t("parentGate.resetSent")}</MutedText>;
  }
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={() => void send()}
        disabled={state === "sending"}
        className="tap-target text-xs font-heading font-semibold text-violet-muted underline underline-offset-2 disabled:opacity-50"
      >
        {t("parentGate.forgot")}
      </button>
      {state === "error" && <p className="text-coral text-xs text-center">{t("parentGate.resetError")}</p>}
    </div>
  );
}

function Gate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { unlock } = useParentGate();
  const [message, setMessage] = useState<string | undefined>(undefined);

  async function checkPin(pin: string): Promise<boolean> {
    try {
      const { ok } = await verifyKidPin(pin);
      if (ok) {
        unlock();
      } else {
        setMessage(undefined);
      }
      return ok;
    } catch (err) {
      // Trop d'essais : message dédié. Toute autre erreur (réseau compris)
      // se lit comme un code refusé, pour que le pavé efface sa saisie plutôt
      // que de rester bloqué (même choix que `PinGate` du mode enfant).
      setMessage(isRateLimitedError(err) ? t("parentGate.rateLimited") : undefined);
      return false;
    }
  }

  return (
    <PageLayout className="flex flex-col items-center justify-center gap-4 px-6">
      <PinPad
        title={t("parentGate.title")}
        subtitle={t("parentGate.subtitle")}
        onSubmit={checkPin}
        errorMessage={message}
      />
      <ForgotPin />
      <Button variant="ghost" onClick={() => navigate("/")}>
        {t("common.back")}
      </Button>
    </PageLayout>
  );
}

export function ParentGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, me } = useAuth();
  const { unlocked } = useParentGate();

  // Déconnecté (ex. /plus, page vitrine publique) : il n'y a pas de code à
  // demander. Les routes sous `RequireAuth` ne passent jamais ici ainsi.
  if (!isAuthenticated) return <>{children}</>;
  // Profil pas encore chargé : on ne sait pas s'il y a un code, donc on
  // n'affiche rien plutôt que de laisser entrevoir l'écran protégé.
  if (!me) return null;
  if (!me.kid_pin_set || unlocked) return <>{children}</>;
  return <Gate />;
}
